import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Home, FileText, CreditCard, LineChart, UserCircle2, Plus, Receipt, ChevronRight, ScanLine, CheckCircle2, Star, Barcode, Ticket, ChevronDown, X, Map, Calendar, Users, Tag, Percent, Sparkles, Smartphone, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import ScanReceiptModal from '../components/ScanReceiptModal';
import ReceiptDetailModal from '../components/ReceiptDetailModal';
import CardDetailSheet, { getBrandGradient } from '../components/CardDetailSheet';
import ReceiptStatusBadge, { getReceiptTapAction } from '../components/ReceiptStatusBadge';
import CouldntReadModal from '../components/CouldntReadModal';
import BuddyLogo, { BUDDY_HI_IMG } from '../components/BuddyLogo';
import OnboardingBlock from '../components/onboarding/OnboardingBlock';
import QuickActionsRow from '../components/QuickActionsRow';
import { hasSeenInsights, isWhatsNewCirclesDismissed, dismissWhatsNewCircles } from '../utils/onboarding';
import axios from 'axios';
import { toast } from 'sonner';
import {
  getGreeting,
  formatReceiptDate,
  getMerchantAvatar,
  getCategoryPill,
  getCardGradient,
  getGroupTypeConfig,
  getMemberAvatarColor,
} from '../utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GRADIENT_HEADER = 'linear-gradient(135deg, #2D5BFF 0%, #1B3DBF 100%)';
const PRIMARY = '#2D5BFF';

// ---------- Currency helpers ----------
const CURRENCY_META = {
  GBP: { symbol: '£', bg: '#EEF2FF', color: '#2D5BFF', activeBg: '#2D5BFF', activeColor: '#FFFFFF' },
  INR: { symbol: '₹', bg: '#F0FDF4', color: '#16A34A', activeBg: '#16A34A', activeColor: '#FFFFFF' },
  USD: { symbol: '$', bg: '#FFF7ED', color: '#EA580C', activeBg: '#EA580C', activeColor: '#FFFFFF' },
  EUR: { symbol: '€', bg: '#FAF5FF', color: '#9333EA', activeBg: '#9333EA', activeColor: '#FFFFFF' },
  AED: { symbol: 'د.إ', bg: '#ECFDF5', color: '#059669', activeBg: '#059669', activeColor: '#FFFFFF' },
  AUD: { symbol: 'A$', bg: '#FFF1F2', color: '#E11D48', activeBg: '#E11D48', activeColor: '#FFFFFF' },
};
const DEFAULT_CURRENCY_META = { symbol: '', bg: '#F3F4F6', color: '#374151', activeBg: '#374151', activeColor: '#FFFFFF' };

const getCurrencyMeta = (currency) => CURRENCY_META[(currency || 'GBP').toUpperCase()] || DEFAULT_CURRENCY_META;

const formatCurrency = (amount, currency) => {
  const meta = getCurrencyMeta(currency);
  return `${meta.symbol}${(amount || 0).toFixed(2)}`;
};

// ---------- Reusable user avatar (image > initial) ----------
const UserAvatar = ({ name, avatarUrl, size = 44, fontSize = 16, bg = PRIMARY, textColor = '#FFFFFF' }) => {
  const initial = (name || 'U').charAt(0).toUpperCase();
  const [imgOk, setImgOk] = useState(true);
  const showImg = avatarUrl && imgOk;
  return (
    <div
      className="flex items-center justify-center overflow-hidden flex-shrink-0"
      style={{
        width: size,
        height: size,
        borderRadius: 999,
        backgroundColor: showImg ? 'transparent' : bg,
        color: textColor,
        fontWeight: 700,
        fontSize,
        fontFamily: 'Manrope, sans-serif',
        textTransform: 'uppercase',
      }}
      data-testid="user-avatar"
    >
      {showImg ? (
        <img src={avatarUrl} alt="" className="w-full h-full object-cover" onError={() => setImgOk(false)} />
      ) : (
        initial
      )}
    </div>
  );
};

// ---------- Buddy logo (waving) ----------
// ---------- Small subcomponents ----------


const MerchantAvatar = ({ name }) => {
  const { initials, bgColor, textColor } = getMerchantAvatar(name);
  return (
    <div
      className="flex items-center justify-center flex-shrink-0"
      style={{
        width: 44,
        height: 44,
        borderRadius: 10,
        backgroundColor: bgColor,
        color: textColor,
        fontWeight: 600,
        fontSize: 14,
        fontFamily: 'Manrope, sans-serif',
      }}
      data-testid={`merchant-avatar-${initials}`}
    >
      {initials}
    </div>
  );
};

const ReceiptRow = ({ receipt, onClick }) => {
  const pill = getCategoryPill(receipt.category);
  const isPending = receipt.scan_status === 'pending_retry' || receipt.scan_status === 'needs_review';
  const isFailed = receipt.scan_status === 'failed';

  return (
    <button
      onClick={() => onClick(receipt)}
      className="w-full flex items-center justify-between py-3 transition-colors hover:bg-gray-50 rounded-lg px-2 -mx-2"
      data-testid={`receipt-item-${receipt.id}`}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <MerchantAvatar name={receipt.merchant} />
        <div className="text-left min-w-0 flex-1">
          <p
            className="font-semibold truncate"
            style={{ color: '#111827', fontSize: 16, fontFamily: 'Manrope, sans-serif' }}
          >
            {isPending ? 'Processing…' : isFailed ? (receipt.merchant && receipt.merchant !== 'Processing…' ? receipt.merchant : 'Unknown') : receipt.merchant}
          </p>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {isPending || isFailed ? (
              <ReceiptStatusBadge status={receipt.scan_status} size="xs" />
            ) : (
              <>
                <ReceiptStatusBadge status={receipt.scan_status} compact={true} />
                {receipt.category ? (
                  <span
                    className="rounded-full"
                    style={{
                      backgroundColor: pill.bg,
                      color: pill.color,
                      fontSize: 11,
                      fontWeight: 600,
                      padding: '2px 8px',
                      fontFamily: 'DM Sans, sans-serif',
                    }}
                  >
                    {receipt.category}
                  </span>
                ) : null}
                <span
                  style={{ color: '#9CA3AF', fontSize: 13, fontFamily: 'DM Sans, sans-serif' }}
                >
                  {formatReceiptDate(receipt.transaction_date || receipt.created_at)}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {/* Hide £0.00 for pending; hide amount for failed */}
        {!isPending && !isFailed && (
          <p
            className="font-semibold"
            style={{ color: '#111827', fontSize: 16, fontFamily: 'Manrope, sans-serif' }}
          >
            {formatCurrency(receipt.amount, receipt.currency || activeCurrency)}
          </p>
        )}
        <ChevronRight className="w-5 h-5" style={{ color: '#D1D5DB' }} />
      </div>
    </button>
  );
};

const SkeletonRow = () => (
  <div className="flex items-center justify-between py-3 animate-pulse">
    <div className="flex items-center gap-3">
      <div style={{ width: 44, height: 44, borderRadius: 10, backgroundColor: '#E5E7EB' }} />
      <div className="space-y-2">
        <div style={{ width: 120, height: 14, borderRadius: 6, backgroundColor: '#E5E7EB' }} />
        <div style={{ width: 70, height: 12, borderRadius: 6, backgroundColor: '#F3F4F6' }} />
      </div>
    </div>
    <div style={{ width: 60, height: 14, borderRadius: 6, backgroundColor: '#E5E7EB' }} />
  </div>
);

const SummaryCard = ({ summary, loading }) => {
  return (
    <div
      className="bg-white"
      style={{
        borderRadius: 16,
        padding: 20,
        boxShadow: '0px 10px 30px rgba(0,0,0,0.1)',
      }}
      data-testid="summary-card"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p style={{ color: '#6B7280', fontSize: 14, fontFamily: 'DM Sans, sans-serif' }}>
            Spent this month
          </p>
          {loading ? (
            <div
              className="mt-2 animate-pulse"
              style={{ width: 140, height: 36, borderRadius: 8, backgroundColor: '#E5E7EB' }}
            />
          ) : (
            <p
              className="mt-1"
              style={{
                color: '#111827',
                fontSize: 32,
                fontWeight: 700,
                fontFamily: 'Manrope, sans-serif',
                lineHeight: 1.1,
              }}
              data-testid="summary-amount"
            >
              {formatCurrency(summary?.total || 0, activeCurrency)}
            </p>
          )}
          <p
            className="mt-2"
            style={{ color: '#9CA3AF', fontSize: 13, fontFamily: 'DM Sans, sans-serif' }}
          >
            {monthLabel}
          </p>
        </div>
        <div className="text-right flex-shrink-0">
          <p style={{ color: '#6B7280', fontSize: 14, fontFamily: 'DM Sans, sans-serif' }}>
            Receipts
          </p>
          {loading ? (
            <div
              className="mt-2 ml-auto animate-pulse"
              style={{ width: 40, height: 24, borderRadius: 6, backgroundColor: '#E5E7EB' }}
            />
          ) : (
            <p
              className="mt-1"
              style={{
                color: '#111827',
                fontSize: 22,
                fontWeight: 600,
                fontFamily: 'Manrope, sans-serif',
              }}
              data-testid="summary-count"
            >
              {summary?.count || 0}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const EmptyState = ({ onScanClick }) => (
  <div className="flex flex-col items-center justify-center py-12 px-4">
    <div
      className="flex items-center justify-center mb-4"
      style={{ width: 72, height: 72, borderRadius: 999, backgroundColor: '#DBEAFE' }}
    >
      <Receipt className="w-8 h-8" style={{ color: PRIMARY }} />
    </div>
    <h3
      className="mb-1 text-center"
      style={{ color: '#111827', fontSize: 18, fontWeight: 600, fontFamily: 'Manrope, sans-serif' }}
    >
      No receipts yet
    </h3>
    <p
      className="mb-5 text-center"
      style={{ color: '#6B7280', fontSize: 14, fontFamily: 'DM Sans, sans-serif' }}
    >
      Start by scanning your first receipt
    </p>
    <Button
      onClick={onScanClick}
      className="rounded-full"
      style={{ backgroundColor: PRIMARY, color: '#FFFFFF', height: 44, padding: '0 24px' }}
      data-testid="empty-scan-button"
    >
      <Plus className="w-4 h-4 mr-2" />
      Scan Receipt
    </Button>
  </div>
);

const GroupTabDisabled = ({ className = '', style = {} }) => (
  <button
    disabled
    title="Circle expenses coming soon"
    className={`rounded-full font-medium ${className}`}
    style={{
      backgroundColor: '#F3F4F6',
      color: '#9CA3AF',
      cursor: 'not-allowed',
      fontFamily: 'DM Sans, sans-serif',
      ...style,
    }}
    data-testid="group-tab-disabled"
  >
    Group / Event
  </button>
);

const FAB = ({ onClick, bottom = 24 }) => (
  <button
    onClick={onClick}
    className="fixed right-5 flex items-center justify-center z-40 transition-transform active:scale-95"
    style={{
      bottom,
      width: 56,
      height: 56,
      borderRadius: 16,
      background: GRADIENT_HEADER,
      boxShadow: '0px 8px 20px rgba(0,0,0,0.2)',
    }}
    data-testid="fab-button"
    aria-label="Scan receipt"
  >
    <Plus className="w-7 h-7 text-white" />
  </button>
);

// ---------- Loyalty promo banner (no cards state) ----------
const LoyaltyPromoBanner = ({ onClick, fullWidthCta = false }) => (
  <button
    onClick={onClick}
    className="w-full flex items-center gap-3 text-left"
    style={{
      background: 'linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)',
      border: '1px solid #C7D2FE',
      borderRadius: 18,
      padding: 16,
    }}
    data-testid="loyalty-promo-banner"
  >
    <div
      className="flex items-center justify-center flex-shrink-0"
      style={{
        width: 52,
        height: 52,
        borderRadius: 14,
        backgroundColor: 'rgba(45,91,255,0.12)',
      }}
    >
      <Star className="w-6 h-6" style={{ color: '#2D5BFF' }} fill="none" strokeWidth={2} />
    </div>
    <div className="flex-1 min-w-0">
      <p
        className="font-bold"
        style={{ color: '#1E3A8A', fontSize: 15, fontFamily: 'Manrope, sans-serif' }}
      >
        Never lose a loyalty card again
      </p>
      <p
        className="mt-0.5"
        style={{ color: '#3B4EA0', fontSize: 13, fontFamily: 'DM Sans, sans-serif' }}
      >
        Store Tesco, Nectar, Boots &amp; more{fullWidthCta ? ' — scan at checkout in a tap' : ''}
      </p>
      {!fullWidthCta ? (
        <span
          className="inline-flex items-center mt-3 rounded-full"
          style={{
            backgroundColor: '#2D5BFF',
            color: '#FFFFFF',
            padding: '6px 14px',
            fontSize: 13,
            fontWeight: 700,
            fontFamily: 'DM Sans, sans-serif',
          }}
          data-testid="loyalty-promo-cta"
        >
          Add a card →
        </span>
      ) : null}
    </div>
    {fullWidthCta ? (
      <span
        className="flex-shrink-0 inline-flex items-center rounded-full"
        style={{
          backgroundColor: '#2D5BFF',
          color: '#FFFFFF',
          padding: '10px 20px',
          fontSize: 14,
          fontWeight: 700,
          fontFamily: 'DM Sans, sans-serif',
        }}
        data-testid="loyalty-promo-cta"
      >
        Add a card →
      </span>
    ) : null}
  </button>
);

const LoyaltyCardTile = ({ card, width = 160, height = 90 }) => {
  const name = card?.card_name || card?.name || '?';
  const initials = name.slice(0, 2).toUpperCase();
  const last4 = card?.barcode_number?.slice(-4) || '';
  // Brand colour when recognised, palette fallback for unknown brands
  const _brand = getBrandGradient(name);
  const _tileGradient = _brand
    ? `linear-gradient(135deg, ${_brand.from} 0%, ${_brand.to} 100%)`
    : getCardGradient(card);
  return (
    <div
      className="relative flex-shrink-0 overflow-hidden"
      style={{
        width,
        height,
        borderRadius: 14,
        background: _tileGradient,
        boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
      }}
      data-testid={`loyalty-card-${card?.id || name}`}
    >
      <div className="absolute inset-0" style={{ padding: 12 }}>
        <div className="flex items-start justify-between">
          <div
            className="flex items-center justify-center"
            style={{
              width: 26,
              height: 26,
              borderRadius: 999,
              backgroundColor: 'rgba(255,255,255,0.2)',
              color: '#FFFFFF',
              fontSize: 10,
              fontWeight: 700,
              fontFamily: 'Manrope, sans-serif',
            }}
          >
            {initials}
          </div>
          <span
            style={{
              color: 'rgba(255,255,255,0.8)',
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: 1,
              fontFamily: 'DM Sans, sans-serif',
            }}
          >
            LOYALTY
          </span>
        </div>
        {/* Name constrained to avoid overlapping barcode button (28px + 10px right + gap) */}
        <div className="absolute" style={{ bottom: 12, left: 12, right: 50 }}>
          <p
            style={{
              color: '#FFFFFF',
              fontSize: 15,
              fontWeight: 700,
              fontFamily: 'Manrope, sans-serif',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {name}
          </p>
          {last4 ? (
            <p
              style={{
                color: 'rgba(255,255,255,0.85)',
                fontSize: 11,
                fontWeight: 600,
                fontFamily: 'DM Sans, sans-serif',
                marginTop: 2,
                letterSpacing: 1,
              }}
            >
              •••• {last4}
            </p>
          ) : null}
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); /* future: card detail */ }}
          className="absolute flex items-center justify-center"
          style={{
            bottom: 10,
            right: 10,
            width: 28,
            height: 28,
            borderRadius: 999,
            backgroundColor: 'rgba(255,255,255,0.25)',
          }}
          data-testid="loyalty-card-barcode-btn"
          aria-label="Barcode"
        >
          <Barcode className="w-4 h-4" style={{ color: '#FFFFFF' }} />
        </button>
      </div>
    </div>
  );
};

// ---------- Add-card dashed tile ----------
const AddCardTile = ({ onClick, width = 160, height = 90 }) => (
  <button
    onClick={onClick}
    className="flex-shrink-0 flex flex-col items-center justify-center gap-1"
    style={{
      width,
      height,
      borderRadius: 14,
      border: '2px dashed #CBD5E1',
      backgroundColor: '#F8FAFC',
      color: '#64748B',
    }}
    data-testid="add-card-tile"
  >
    <Plus className="w-5 h-5" />
    <span style={{ fontSize: 12, fontWeight: 600, fontFamily: 'DM Sans, sans-serif' }}>Add card</span>
  </button>
);

// ---------- Loyalty cards strip ----------
const LoyaltyCardsStrip = ({ cards, onSeeAll, onAdd, onCardClick, cardSize }) => (
  <div className="space-y-3">
    <div className="flex items-center justify-between">
      <h2 style={{ color: '#111827', fontSize: 16, fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>
        Your cards
      </h2>
      <button
        onClick={onSeeAll}
        style={{ color: '#2D5BFF', fontSize: 14, fontWeight: 600, fontFamily: 'DM Sans, sans-serif' }}
        data-testid="cards-see-all"
      >
        See all
      </button>
    </div>
    <div className="flex items-center gap-3 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
      {cards.map((c) => (
        <div key={c.id || c.card_name} onClick={() => onCardClick?.(c)} style={{ cursor: 'pointer' }}>
          <LoyaltyCardTile card={c} width={cardSize?.w} height={cardSize?.h} />
        </div>
      ))}
      <AddCardTile onClick={onAdd} width={cardSize?.w} height={cardSize?.h} />
    </div>
  </div>
);

// ---------- Loyalty cards 2x2 grid (desktop) ----------
const LoyaltyCardsGrid = ({ cards, onSeeAll, onAdd, onCardClick }) => (
  <div className="space-y-3">
    <div className="flex items-center justify-between">
      <h2 style={{ color: '#111827', fontSize: 16, fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>
        Your cards
      </h2>
      <button
        onClick={onSeeAll}
        style={{ color: '#2D5BFF', fontSize: 14, fontWeight: 600, fontFamily: 'DM Sans, sans-serif' }}
        data-testid="cards-see-all"
      >
        See all
      </button>
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
      {cards.slice(0, 4).map((c) => (
        <div key={c.id || c.card_name} onClick={() => onCardClick?.(c)} style={{ cursor: 'pointer' }}>
          <LoyaltyCardTile card={c} width="100%" height={110} />
        </div>
      ))}
      {cards.length < 5 && (
        <AddCardTile onClick={onAdd} width="100%" height={110} />
      )}
    </div>
  </div>
);

// ---------- Voucher summary card (dashboard) ----------

const getMerchantInitialsVoucher = (name) => {
  if (!name) return '?';
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const VOUCHER_RETAILER_COLORS = {
  'Tesco':        { bg: '#1C3FAA', text: '#FFFFFF' },
  "Sainsbury's":  { bg: '#F47721', text: '#FFFFFF' },
  'B&Q':          { bg: '#1A1A1A', text: '#FFFFFF' },
  'M&S':          { bg: '#1A1A1A', text: '#FFFFFF' },
  'Boots':        { bg: '#005B3A', text: '#FFFFFF' },
  'Nectar':       { bg: '#6B21A8', text: '#FFFFFF' },
  'Asda':         { bg: '#78BE20', text: '#FFFFFF' },
  'Waitrose':     { bg: '#5D8C3E', text: '#FFFFFF' },
  'Morrisons':    { bg: '#006F42', text: '#FFFFFF' },
  'Lidl':         { bg: '#0050AA', text: '#FFD700' },
  'Aldi':         { bg: '#00205B', text: '#FFFFFF' },
  'Co-op':        { bg: '#00B1EB', text: '#FFFFFF' },
};

const getRetailerColor = (name) => {
  if (!name) return { bg: '#6B7280', text: '#FFFFFF' };
  if (VOUCHER_RETAILER_COLORS[name]) return VOUCHER_RETAILER_COLORS[name];
  const lower = name.toLowerCase();
  for (const [key, val] of Object.entries(VOUCHER_RETAILER_COLORS)) {
    if (lower.includes(key.toLowerCase())) return val;
  }
  const charCode = name.charCodeAt(0) || 65;
  const hue = (charCode * 137) % 360;
  return { bg: `hsl(${hue}, 55%, 45%)`, text: '#FFFFFF' };
};

const VoucherSummaryCard = ({ summary, onPress, onAddClick, isMobile = true }) => {
  const hasVouchers = summary && summary.active_count > 0;

  // Empty state — QuickActionsRow Voucher button already handles discovery
  if (!hasVouchers) return null;

  // Populated state
  const retailers    = summary.retailers       || [];
  const expiringSoon = summary.expiring_soon   || 0;
  const currencySymbol = summary.savings_currency === 'USD' ? '$' : summary.savings_currency === 'EUR' ? '€' : '£';

  // Type-split savings fields from backend
  const moneySavings    = summary.money_savings    ?? null;
  const moneyCount      = summary.money_count      || 0;
  const bestPercentage  = summary.best_percentage  ?? null;
  const percentageCount = summary.percentage_count || 0;
  const bestPoints      = summary.best_points      ?? null;
  const pointsCount     = summary.points_count     || 0;

  // Only render slots that have data
  const slots = [
    moneySavings !== null && {
      key: 'money',
      icon: <Tag className="w-5 h-5" style={{ color: '#FFFFFF' }} strokeWidth={2} />,
      iconBg: 'rgba(147,197,253,0.30)',
      value: `${currencySymbol}${moneySavings.toFixed(2)}`,
      suffix: 'Off',
      label: 'Money Off',
      count: moneyCount,
    },
    bestPercentage !== null && {
      key: 'pct',
      icon: <Percent className="w-5 h-5" style={{ color: '#FFFFFF' }} strokeWidth={2} />,
      iconBg: 'rgba(110,231,183,0.30)',
      value: `${bestPercentage}%`,
      suffix: 'Off',
      label: 'Percentage Off',
      count: percentageCount,
    },
    bestPoints !== null && {
      key: 'pts',
      icon: <Star className="w-5 h-5" style={{ color: '#FFFFFF' }} strokeWidth={2} />,
      iconBg: 'rgba(196,181,253,0.30)',
      value: 'Bonus',
      suffix: 'Points',
      label: 'Loyalty Rewards',
      count: pointsCount,
    },
  ].filter(Boolean);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 style={{ color: '#111827', fontSize: 16, fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>
          Your vouchers
        </h2>
        <button
          onClick={onPress}
          style={{ color: '#2D5BFF', fontSize: 14, fontWeight: 600, fontFamily: 'DM Sans, sans-serif' }}
          data-testid="vouchers-see-all"
        >
          See all
        </button>
      </div>
      <button
        onClick={onPress}
        className="w-full text-left"
        style={{
          background: 'linear-gradient(135deg, #2D5BFF 0%, #1B3DBF 100%)',
          borderRadius: 18,
          padding: isMobile ? '16px 18px' : '20px 24px',
          boxShadow: '0 6px 20px rgba(45,91,255,0.3)',
        }}
        data-testid="voucher-summary-card"
      >
        {/* Top row: title + expiring badge */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Ticket className="w-5 h-5" style={{ color: 'rgba(255,255,255,0.9)' }} strokeWidth={2} />
            <span style={{ color: '#FFFFFF', fontSize: 16, fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>
              Vouchers
            </span>
          </div>
          {expiringSoon > 0 && (
            <span
              className="rounded-full"
              style={{
                backgroundColor: '#F59E0B',
                color: '#FFFFFF',
                padding: '4px 12px',
                fontSize: 12,
                fontWeight: 700,
                fontFamily: 'DM Sans, sans-serif',
              }}
              data-testid="voucher-expiring-badge"
            >
              {expiringSoon} expiring soon
            </span>
          )}
        </div>

        {/* Type slots — dynamic, only what exists */}
        <div className="flex items-stretch" style={{ gap: slots.length === 1 ? 0 : 1 }}>
          {slots.map((slot, idx) => (
            <div
              key={slot.key}
              className="flex-1 flex flex-col items-center text-center"
              style={{
                paddingLeft: idx === 0 ? 0 : 12,
                paddingRight: idx === slots.length - 1 ? 0 : 12,
                borderLeft: idx > 0 ? '1px solid rgba(255,255,255,0.18)' : 'none',
              }}
            >
              {/* Value */}
              <p style={{ color: '#FFFFFF', fontFamily: 'Manrope, sans-serif', lineHeight: 1.1, margin: 0 }}>
                <span style={{ fontSize: isMobile ? 19 : 23, fontWeight: 700 }}>{slot.value}</span>
                {' '}
                <span style={{ fontSize: 12, fontWeight: 500, opacity: 0.85 }}>{slot.suffix}</span>
              </p>
              {/* Label */}
              <p className="mt-1" style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11, fontFamily: 'DM Sans, sans-serif', margin: '3px 0 0 0' }}>
                {slot.label}
              </p>
              {/* Count badge */}
              <span
                className="rounded-full"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  color: 'rgba(255,255,255,0.9)',
                  padding: '2px 6px',
                  fontSize: 11,
                  fontWeight: 600,
                  fontFamily: 'DM Sans, sans-serif',
                  marginTop: 4,
                  display: 'inline-block',
                }}
              >
                {slot.count} voucher{slot.count !== 1 ? 's' : ''}
              </span>
            </div>
          ))}
        </div>

        {/* Bottom row: retailer avatars + text */}
        {retailers.length > 0 && (
          <div
            className="flex items-center justify-between mt-4 pt-3"
            style={{ borderTop: '1px solid rgba(255,255,255,0.15)' }}
          >
            <div className="flex items-center gap-2 min-w-0">
              <div className="flex -space-x-1.5">
                {retailers.slice(0, 4).map((name, i) => {
                  const rc = getRetailerColor(name);
                  return (
                    <div
                      key={i}
                      className="flex items-center justify-center flex-shrink-0"
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 8,
                        backgroundColor: rc.bg,
                        color: rc.text,
                        fontSize: 10,
                        fontWeight: 700,
                        fontFamily: 'Manrope, sans-serif',
                        border: '2px solid rgba(45,91,255,0.8)',
                        zIndex: retailers.length - i,
                      }}
                    >
                      {getMerchantInitialsVoucher(name)}
                    </div>
                  );
                })}
              </div>
              <span className="truncate" style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13, fontFamily: 'DM Sans, sans-serif' }}>
                Use them before they expire
              </span>
            </div>
            <ChevronRight className="w-5 h-5 flex-shrink-0" style={{ color: 'rgba(255,255,255,0.5)' }} />
          </div>
        )}
      </button>
    </div>
  );
};

// ---------- Currency filter chips ----------
const CurrencyChips = ({ currencies, selected, onSelect }) => {
  if (!currencies || currencies.length === 0) return null;
  return (
    <div
      className="flex items-center gap-2 overflow-x-auto"
      style={{ scrollbarWidth: 'none', paddingBottom: 2 }}
      data-testid="currency-chips"
    >
      {currencies.map((cur) => {
        const meta = getCurrencyMeta(cur);
        const isSelected = cur === selected;
        return (
          <button
            key={cur}
            onClick={() => onSelect(cur)}
            className="flex-shrink-0 flex items-center gap-1.5 rounded-full transition-all"
            style={{
              backgroundColor: isSelected ? meta.activeBg : meta.bg,
              color: isSelected ? meta.activeColor : meta.color,
              padding: '6px 14px',
              fontSize: 13,
              fontWeight: 600,
              fontFamily: 'DM Sans, sans-serif',
              border: `1.5px solid ${isSelected ? meta.activeBg : meta.color}22`,
              boxShadow: isSelected ? '0 2px 8px rgba(0,0,0,0.12)' : 'none',
            }}
            data-testid={`currency-chip-${cur}`}
          >
            <span style={{ fontSize: 12, opacity: isSelected ? 1 : 0.8 }}>{meta.symbol}</span>
            {cur}
          </button>
        );
      })}
    </div>
  );
};

// ---------- Group Switcher Pill (top-right dropdown button) ----------
const GroupSwitcherPill = ({ selectedGroup, avatarUrl, userName, onClick }) => {
  if (selectedGroup) {
    const cfg = getGroupTypeConfig(selectedGroup.type);
    const Icon = cfg.icon;
    return (
      <button onClick={onClick} className="flex items-center gap-2" style={{
        backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 999,
        padding: '6px 14px 6px 8px', flexShrink: 0,
      }}>
        <div className="flex items-center justify-center" style={{
          width: 28, height: 28, borderRadius: 999, backgroundColor: cfg.bg, fontSize: 15,
        }}>
          {selectedGroup.emoji
            ? <span style={{ lineHeight: 1 }}>{selectedGroup.emoji}</span>
            : <Icon className="w-3.5 h-3.5" style={{ color: '#FFFFFF' }} />}
        </div>
        <span className="truncate" style={{
          color: '#FFFFFF', fontSize: 14, fontWeight: 600,
          fontFamily: 'DM Sans, sans-serif', maxWidth: 100,
        }}>{selectedGroup.name}</span>
        <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: 'rgba(255,255,255,0.7)' }} />
      </button>
    );
  }
  // Personal mode
  const initial = (userName || 'U').charAt(0).toUpperCase();
  return (
    <button onClick={onClick} className="flex items-center gap-2" style={{
      backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 999,
      padding: '6px 14px 6px 8px', flexShrink: 0,
    }}>
      <div className="flex items-center justify-center overflow-hidden" style={{
        width: 28, height: 28, borderRadius: 999,
        backgroundColor: avatarUrl ? 'transparent' : 'rgba(255,255,255,0.2)',
        color: '#FFFFFF', fontWeight: 700, fontSize: 11, fontFamily: 'Manrope, sans-serif',
      }}>
        {avatarUrl ? <img src={avatarUrl} alt="" className="w-full h-full object-cover" /> : initial}
      </div>
      <span style={{ color: '#FFFFFF', fontSize: 14, fontWeight: 600, fontFamily: 'DM Sans, sans-serif' }}>Personal</span>
      <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: 'rgba(255,255,255,0.7)' }} />
    </button>
  );
};

// ---------- Group Switcher Bottom Sheet ----------
const GroupSwitcherSheet = ({ isOpen, onClose, groups, selectedGroupId, onSelect, onCreateGroup, onManageGroups, userName, avatarUrl }) => {
  if (!isOpen) return null;
  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} className="fixed inset-0 z-50" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }} />
      {/* Sheet */}
      <div className="fixed left-0 right-0 bottom-0 z-50" style={{
        backgroundColor: '#FFFFFF', borderTopLeftRadius: 24, borderTopRightRadius: 24,
        padding: '12px 20px 0', maxHeight: '70vh', overflowY: 'auto',
        boxShadow: '0 -8px 32px rgba(0,0,0,0.15)',
        paddingBottom: 'calc(32px + env(safe-area-inset-bottom, 0px))',
      }}>
        {/* Handle bar */}
        <div className="flex justify-center mb-4">
          <div style={{ width: 36, height: 4, borderRadius: 999, backgroundColor: '#D1D5DB' }} />
        </div>
        <p style={{ color: '#6B7280', fontSize: 12, fontWeight: 700, fontFamily: 'DM Sans, sans-serif', letterSpacing: 1, marginBottom: 16 }}>
          SWITCH TO
        </p>

        {/* Personal option */}
        <button onClick={() => onSelect(null)} className="w-full flex items-center gap-3 py-3" style={{ borderBottom: '1px solid #F3F4F6' }}>
          <div className="flex items-center justify-center overflow-hidden flex-shrink-0" style={{
            width: 40, height: 40, borderRadius: 999,
            backgroundColor: avatarUrl ? 'transparent' : PRIMARY, color: '#FFFFFF',
            fontWeight: 700, fontSize: 14, fontFamily: 'Manrope, sans-serif',
          }}>
            {avatarUrl ? <img src={avatarUrl} alt="" className="w-full h-full object-cover" /> : (userName || 'U').charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0 text-left">
            <p style={{ color: '#111827', fontSize: 15, fontWeight: 600, fontFamily: 'Manrope, sans-serif' }}>Personal</p>
            <p style={{ color: '#9CA3AF', fontSize: 13, fontFamily: 'DM Sans, sans-serif', marginTop: 1 }}>Just your receipts, cards &amp; vouchers</p>
          </div>
          {!selectedGroupId && <CheckCircle2 className="w-5 h-5 flex-shrink-0" style={{ color: PRIMARY }} />}
        </button>

        {/* Group options */}
        {groups.map((g) => {
          const cfg = getGroupTypeConfig(g.type);
          const Icon = cfg.icon;
          const isActive = selectedGroupId === g.id;
          return (
            <button key={g.id} onClick={() => onSelect(g)} className="w-full flex items-center gap-3 py-3" style={{ borderBottom: '1px solid #F3F4F6' }}>
              <div className="flex items-center justify-center flex-shrink-0" style={{
                width: 40, height: 40, borderRadius: 999, backgroundColor: cfg.bg, fontSize: 20,
              }}>
                {g.emoji
                  ? <span style={{ lineHeight: 1 }}>{g.emoji}</span>
                  : <Icon className="w-5 h-5" style={{ color: '#FFFFFF' }} />}
              </div>
              <div className="flex-1 min-w-0 text-left">
                <p style={{ color: '#111827', fontSize: 15, fontWeight: 600, fontFamily: 'Manrope, sans-serif' }}>{g.name}</p>
                <p style={{ color: '#9CA3AF', fontSize: 13, fontFamily: 'DM Sans, sans-serif', marginTop: 1 }}>
                  {cfg.label} · {g.member_count} member{g.member_count !== 1 ? 's' : ''}
                </p>
              </div>
              {isActive && <CheckCircle2 className="w-5 h-5 flex-shrink-0" style={{ color: PRIMARY }} />}
            </button>
          );
        })}

        {/* Actions */}
        <div style={{ marginTop: 8 }}>
          <button onClick={onCreateGroup} className="w-full flex items-center gap-3 py-3">
            <div className="flex items-center justify-center flex-shrink-0" style={{
              width: 40, height: 40, borderRadius: 999, backgroundColor: '#F3F4F6',
            }}>
              <Plus className="w-5 h-5" style={{ color: PRIMARY }} />
            </div>
            <p style={{ color: PRIMARY, fontSize: 15, fontWeight: 600, fontFamily: 'Manrope, sans-serif' }}>Create a circle</p>
          </button>
          <button onClick={onManageGroups} className="w-full flex items-center gap-3 py-3">
            <div className="flex items-center justify-center flex-shrink-0" style={{
              width: 40, height: 40, borderRadius: 999, backgroundColor: '#F3F4F6',
            }}>
              <Users className="w-5 h-5" style={{ color: '#6B7280' }} />
            </div>
            <p style={{ color: '#6B7280', fontSize: 15, fontWeight: 600, fontFamily: 'Manrope, sans-serif' }}>Manage circles</p>
          </button>
        </div>
      </div>
    </>
  );
};

// ---------- Member avatars row ----------
const MemberAvatarsRow = ({ members = [], count = 0 }) => {
  const shown = members.slice(0, 3);
  return (
    <div className="flex items-center gap-2 mt-2">
      <div className="flex items-center">
        {shown.map((m, i) => {
          const name = m.display_name || '?';
          const initials = name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
          const bg = getMemberAvatarColor(name);
          return (
            <div key={i} className="flex items-center justify-center overflow-hidden flex-shrink-0" style={{
              width: 28, height: 28, borderRadius: 999, backgroundColor: bg,
              border: '2px solid rgba(45,91,255,0.8)', marginLeft: i > 0 ? -8 : 0,
              zIndex: shown.length - i, position: 'relative',
              color: '#FFFFFF', fontSize: 10, fontWeight: 700, fontFamily: 'Manrope, sans-serif',
            }}>
              {initials}
            </div>
          );
        })}
      </div>
      <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13, fontFamily: 'DM Sans, sans-serif' }}>
        {count} member{count !== 1 ? 's' : ''} sharing
      </span>
    </div>
  );
};

// ---------- Navigation data ----------

const NAV_ITEMS = [
  { icon: Home, label: 'Home', key: 'home' },
  { icon: FileText, label: 'Receipts', key: 'receipts' },
  { icon: CreditCard, label: 'Cards', key: 'cards' },
  { icon: LineChart, label: 'Insights', key: 'insights' },
  { icon: UserCircle2, label: 'Profile', key: 'profile' },
];

// ---------- Main Page ----------

export default function DashboardPage() {
  const { user: authUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);
  const [showScanModal, setShowScanModal] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({ user: null, receipts: [], monthly_summaries: [], voucher_summary: null });
  const [selectedReceiptId, setSelectedReceiptId] = useState(null);
  const [showReceiptDetail, setShowReceiptDetail] = useState(false);
  const [activeNav, setActiveNav] = useState('home');
  const [loyaltyCards, setLoyaltyCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(null);

  // ── Group switcher state ──────────────────────────────────────────────────
  const [showGroupSwitcher, setShowGroupSwitcher] = useState(false);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupData, setGroupData] = useState(null); // { summary, items, members }
  const [groupLoading, setGroupLoading] = useState(false);

  const isGroupView = !!selectedGroup;

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const fetchDashboard = useCallback(async () => {
    // Clear stale data immediately so the loading state never shows old content
    setLoading(true);
    setDashboardData({ user: null, receipts: [], monthly_summaries: [], voucher_summary: null });
    setLoyaltyCards([]);
    try {
      const [dashRes, cardsRes] = await Promise.all([
        axios.get(`${API}/dashboard`, { withCredentials: true }),
        axios.get(`${API}/cards`, { withCredentials: true }),
      ]);
      setDashboardData(dashRes.data);
      setLoyaltyCards(cardsRes.data.cards || []);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      if (err.response?.status === 401) {
        navigate('/login', { replace: true });
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  // ── Fetch groups list (for switcher) ──────────────────────────────────────
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const res = await axios.get(`${API}/groups`, { withCredentials: true });
        const fetched = res.data.groups || [];
        setGroups(fetched);
        // Restore previously selected group from sessionStorage
        try {
          const saved = sessionStorage.getItem('mb_selected_group');
          if (saved) {
            const parsed = JSON.parse(saved);
            const match = fetched.find(g => g.id === parsed.id);
            if (match) setSelectedGroup(match);
          }
        } catch { /* ignore */ }
      } catch { /* silently fail */ }
    };
    fetchGroups();
  }, []);

  // ── Fetch group data when a group is selected ─────────────────────────────
  useEffect(() => {
    if (!selectedGroup) { setGroupData(null); return; }
    const fetchGroupData = async () => {
      setGroupLoading(true);
      try {
        const [summaryRes, itemsRes] = await Promise.all([
          axios.get(`${API}/groups/${selectedGroup.id}/summary`, { withCredentials: true }),
          axios.get(`${API}/groups/${selectedGroup.id}/items`, { withCredentials: true }),
        ]);
        const items = itemsRes.data.items || [];
        setGroupData({
          summary: summaryRes.data,
          receipts: items.filter(i => i.item_type === 'receipt'),
          cards: items.filter(i => i.item_type === 'card'),
          vouchers: items.filter(i => i.item_type === 'voucher'),
          members: summaryRes.data.members || [],
          spending: summaryRes.data.spending || {},
        });
      } catch {
        toast.error("Couldn't load group data");
        setSelectedGroup(null);
      } finally {
        setGroupLoading(false);
      }
    };
    fetchGroupData();
  }, [selectedGroup]);

  const handleGroupSelect = (group) => {
    setSelectedGroup(group);
    setShowGroupSwitcher(false);
    // Persist selection across pages
    try {
      if (group) sessionStorage.setItem('mb_selected_group', JSON.stringify({ id: group.id }));
      else sessionStorage.removeItem('mb_selected_group');
    } catch { /* ignore */ }
  };

  // ── Auto-refresh while any receipt is still processing ─────────────────────
  useEffect(() => {
    const hasPending = dashboardData?.receipts?.some(
      (r) => r.scan_status === 'pending_retry' || r.scan_status === 'needs_review'
    );
    if (!hasPending) return;
    const id = setInterval(fetchDashboard, 10000);
    return () => clearInterval(id);
  }, [dashboardData?.receipts, fetchDashboard]);

  // ── Access-mode tracking ──────────────────────────────────────────────────
  // Fires once per app session for ALL users (mobile, desktop, browser, standalone).
  // Uses sessionStorage to prevent over-counting when navigating between tabs
  // within the same session — only counts genuine app opens, not tab switches.
  // sessionStorage clears when the tab/window closes, so the next visit counts.
  useEffect(() => {
    const trackAccessMode = async () => {
      try {
        // Already tracked this session? Skip.
        if (sessionStorage.getItem('mybuddy_access_tracked')) return;

        const isStandalone =
          window.matchMedia?.('(display-mode: standalone)')?.matches === true ||
          navigator.standalone === true;
        const mode = isStandalone ? 'webapp' : 'browser';
        await axios.patch(
          `${API}/me/access-mode`,
          { mode },
          { withCredentials: true }
        );

        // Mark this session as tracked
        sessionStorage.setItem('mybuddy_access_tracked', '1');
      } catch (e) {
        // Non-critical — never block app flow.
        // Don't set the flag so it retries next navigation.
        console.warn('[access-mode] tracking failed (ignored):', e);
      }
    };
    trackAccessMode();
  }, []); // only on mount

  const handleScanReceipt = () => {
    setShowScanModal(true);
  };

  const [couldntReadReceipt, setCouldntReadReceipt] = useState(null);

  const handleReceiptClick = (receipt) => {
    const action = getReceiptTapAction(receipt.scan_status);
    if (action === 'processing-toast') {
      toast.info("Still processing this one — check back in a few minutes.", {
        description: "We'll scan it in the background. Usually ready within 15 minutes.",
      });
      return;
    }
    if (action === 'couldnt-read-modal') {
      setCouldntReadReceipt(receipt);
      return;
    }
    // Completed — open normally
    if (isMobile) {
      setSelectedReceiptId(receipt.id);
      setShowReceiptDetail(true);
    } else {
      navigate(`/receipts/${receipt.id}`);
    }
  };

  const handleReceiptDelete = (deletedId) => {
    setDashboardData((prev) => ({
      ...prev,
      receipts: prev.receipts.filter((r) => r.id !== deletedId),
    }));
  };

  // If user came back from detail page via back arrow, reopen the sheet for that receipt
  useEffect(() => {
    const openId = location.state?.openReceiptId;
    if (openId && isMobile) {
      setSelectedReceiptId(openId);
      setShowReceiptDetail(true);
      // clear state so refreshes don't re-trigger
      navigate('.', { replace: true, state: {} });
    }
  }, [location.state, isMobile, navigate]);

  // Set default selected currency once data loads (most recent receipt's currency)
  useEffect(() => {
    if (loading) return;
    if (selectedCurrency) return; // already chosen by user
    const receipts = dashboardData.receipts || [];
    const summaries = dashboardData.monthly_summaries || [];
    const defaultCur =
      (receipts[0]?.currency || summaries[0]?.currency || 'GBP').toUpperCase();
    setSelectedCurrency(defaultCur);
  }, [loading, dashboardData]);

  // Capitalise first letter of each word — safety net for any existing DB records
  // that were saved before the server-side normalisation was added.
  const toTitleCase = (s) => (s || '').replace(/\b[a-z]/g, (c) => c.toUpperCase());

  const userName = toTitleCase(dashboardData.user?.first_name || authUser?.first_name || 'User');
  const lastName = toTitleCase(dashboardData.user?.last_name  || authUser?.last_name  || '');
  const userEmail = dashboardData.user?.email || authUser?.email || '';
  const avatarUrl = dashboardData.user?.avatar_url || authUser?.avatar_url;
  const initial = userName.charAt(0).toUpperCase();

  const sortedReceipts = [...(dashboardData.receipts || [])].sort((a, b) => {
    const da = new Date(a.created_at || a.transaction_date).getTime();
    const db = new Date(b.created_at || b.transaction_date).getTime();
    return db - da;
  });

  // All unique currencies across monthly summaries + top-10 receipts
  const monthlySummaries = dashboardData.monthly_summaries || [];
  const allCurrencies = [...new Set([
    ...monthlySummaries.map((s) => s.currency.toUpperCase()),
    ...sortedReceipts.map((r) => (r.currency || 'GBP').toUpperCase()),
  ])];

  // Current month summary for the selected currency
  const activeCurrency = selectedCurrency || allCurrencies[0] || 'GBP';
  const currentSummary = monthlySummaries.find(
    (s) => s.currency.toUpperCase() === activeCurrency
  ) || { currency: activeCurrency, total: 0, count: 0 };

  // Month label from current date
  const monthLabel = new Date().toLocaleString('default', { month: 'long', year: 'numeric' });

  // Top 5 recent receipts filtered by selected currency
  const recentReceipts = sortedReceipts
    .filter((r) => (r.currency || 'GBP').toUpperCase() === activeCurrency)
    .slice(0, 5);

  const hasCards = loyaltyCards.length > 0;
  const voucherSummary = dashboardData.voucher_summary || null;
  const navDisabled = false; // all tabs always enabled

  const onNavClick = (key) => {
    if (key === 'home') {
      setActiveNav('home');
      return;
    }
    if (key === 'cards') {
      navigate('/cards');
      return;
    }
    if (key === 'receipts') {
      navigate('/receipts');
      return;
    }
    if (key === 'insights') {
      navigate('/insights');
      return;
    }
    if (key === 'profile') {
      navigate('/profile');
      return;
    }
    if (navDisabled) return;
    setActiveNav(key);
    toast.info(`${key.charAt(0).toUpperCase() + key.slice(1)} coming soon`);
  };

  const handleCardsClick = () => {
    if (navDisabled) {
      navigate('/cards');
      return;
    }
    setActiveNav('cards');
    navigate('/cards');
  };

  const handleVouchersClick = () => {
    navigate('/vouchers');
  };

  // ── Header notification icon (WhatsNew → A2HS priority) ──────────────────
  // Single icon slot in the header. WhatsNew takes priority until dismissed,
  // then falls back to A2HS phone icon (mobile browser only, follows cooldown).
  const [whatsNewDismissed, setWhatsNewDismissed] = useState(() => isWhatsNewCirclesDismissed());
  const [showWhatsNewSheet, setShowWhatsNewSheet] = useState(false);

  // Evaluate A2HS visibility synchronously (mirrors AddToHomeScreenPrompt logic)
  const a2hsVisible = (() => {
    if (typeof window === 'undefined') return false;
    if (!/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) return false;
    if (window.matchMedia?.('(display-mode: standalone)')?.matches || navigator.standalone) return false;
    try {
      const ts = localStorage.getItem('mybuddy_a2hs_dismissed_at');
      const count = parseInt(localStorage.getItem('mybuddy_a2hs_dismiss_count') || '0', 10);
      if (ts) {
        const SCHEDULE = [2, 4, 7, 10, 15, 30];
        const cooldown = count > 0 ? SCHEDULE[Math.min(count - 1, SCHEDULE.length - 1)] : 0;
        if ((Date.now() - parseInt(ts, 10)) / 86_400_000 < cooldown) return false;
      }
    } catch {}
    return true;
  })();

  // Priority: 'whatsnew' first, then 'a2hs', then null (icon slot hidden)
  const headerIcon = !whatsNewDismissed ? 'whatsnew' : (a2hsVisible ? 'a2hs' : null);

  const handleWhatsNewDismiss = () => {
    dismissWhatsNewCircles();
    setWhatsNewDismissed(true);
    setShowWhatsNewSheet(false);
  };

  // A2HS taps directly open the guide overlay — no sheet needed
  const handleA2HSTap = () => {
    window.MyBuddyAddToHome?.open();
  };

  // ── Onboarding wiring (all pieces self-gate via localStorage) ─────────────
  const onboardingProps = {
    userName,
    hasReceipts: (dashboardData.receipts || []).length > 0,
    hasCircle: groups.length > 0,
    hasCard: loyaltyCards.length > 0,
    onScan: handleScanReceipt,
    onCreateCircle: () => navigate('/groups/create'),
    onAddCard: () => navigate('/cards'),
    onOpenInsights: () => navigate('/insights'),
  };

  // "First-run setup" is active until the user dismisses the checklist or
  // completes all steps. While active we hide the promotional feature cards
  // (the checklist already routes there) to avoid duplicate CTAs / clutter.
  const gsDoneCount = [
    onboardingProps.hasReceipts,
    onboardingProps.hasCircle,
    onboardingProps.hasCard,
    hasSeenInsights(),
  ].filter(Boolean).length;
  const onboardingActive = gsDoneCount < 4;
  const hasVouchers = !!(voucherSummary && (voucherSummary.active_count || 0) > 0);

  // ------------ MOBILE ------------
  if (isMobile) {
    const isEmpty = !loading && recentReceipts.length === 0;

    // ── Loading screen — shown on every (re)load, prevents stale content flash
    if (loading) {
      return (
        <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#F5F7FA' }} data-testid="dashboard-loading">
          <header style={{ background: GRADIENT_HEADER, padding: '32px 20px 24px 20px', borderBottomLeftRadius: 28, borderBottomRightRadius: 28 }}>
            <div className="flex items-start justify-between">
              <div>
                <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14, fontFamily: 'DM Sans, sans-serif' }}>
                  {getGreeting()}
                </p>
                <h1 className="mt-1" style={{ color: '#FFFFFF', fontSize: 26, fontWeight: 600, fontFamily: 'Manrope, sans-serif', lineHeight: 1.2 }}>
                  Hello, {userName}
                </h1>
              </div>
              <GroupSwitcherPill
                selectedGroup={selectedGroup}
                avatarUrl={avatarUrl}
                userName={userName}
                onClick={() => setShowGroupSwitcher(true)}
              />
            </div>
          </header>
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="w-9 h-9 animate-spin" style={{ color: '#2D5BFF' }} />
          </div>
        </div>
      );
    }

    if (isEmpty) {
      return (
        <div
          className="min-h-screen flex flex-col"
          style={{ backgroundColor: '#FFFFFF' }}
          data-testid="dashboard-page"
        >
          {/* Mobile hero with translucent stat card */}
          <header
            style={{
              background: GRADIENT_HEADER,
              padding: '32px 20px 24px 20px',
              borderBottomLeftRadius: 28,
              borderBottomRightRadius: 28,
            }}
          >
            <div className="flex items-start justify-between mb-5">
              <div>
                <p
                  style={{
                    color: 'rgba(255,255,255,0.8)',
                    fontSize: 14,
                    fontFamily: 'DM Sans, sans-serif',
                  }}
                >
                  {getGreeting()}
                </p>
                <h1
                  className="mt-1"
                  style={{
                    color: '#FFFFFF',
                    fontSize: 26,
                    fontWeight: 600,
                    fontFamily: 'Manrope, sans-serif',
                    lineHeight: 1.2,
                  }}
                  data-testid="dashboard-greeting"
                >
                  Hello, {userName}
                </h1>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {headerIcon && (
                  <button
                    onClick={() => headerIcon === 'whatsnew' ? setShowWhatsNewSheet(true) : handleA2HSTap()}
                    className="relative flex items-center justify-center"
                    style={{ width: 34, height: 34, borderRadius: 999, backgroundColor: 'rgba(255,255,255,0.18)' }}
                    aria-label={headerIcon === 'whatsnew' ? "What's new" : "Add to Home Screen"}
                  >
                    {headerIcon === 'whatsnew' ? (
                      <>
                        <Sparkles className="w-4 h-4" style={{ color: '#FFFFFF' }} />
                        <span className="absolute" style={{ top: 5, right: 5, width: 7, height: 7, borderRadius: 999, backgroundColor: '#F59E0B', border: '1.5px solid rgba(45,91,255,0.7)' }} />
                      </>
                    ) : (
                      <Smartphone className="w-4 h-4" style={{ color: '#FFFFFF' }} />
                    )}
                  </button>
                )}
                <GroupSwitcherPill
                  selectedGroup={selectedGroup}
                  avatarUrl={avatarUrl}
                  userName={userName}
                  onClick={() => setShowGroupSwitcher(true)}
                />
              </div>
            </div>

            {/* Translucent stat card inside hero */}
            <div
              style={{
                backgroundColor: 'rgba(255,255,255,0.15)',
                borderRadius: 14,
                padding: '11px 14px',
              }}
              data-testid="empty-stat-card-mobile"
            >
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12, fontFamily: 'DM Sans, sans-serif' }}>
                    Spent this month
                  </p>
                  <p
                    className="mt-0.5"
                    style={{
                      color: '#FFFFFF',
                      fontSize: 24,
                      fontWeight: 700,
                      fontFamily: 'Manrope, sans-serif',
                      lineHeight: 1.1,
                    }}
                    data-testid="empty-spent-amount"
                  >
                    {formatCurrency(0, activeCurrency)}
                  </p>
                  <p className="mt-1" style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11, fontFamily: 'DM Sans, sans-serif' }} data-testid="empty-month-label">
                    {monthLabel}
                  </p>
                </div>
                <div className="text-right">
                  <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12, fontFamily: 'DM Sans, sans-serif' }}>
                    Receipts
                  </p>
                  <p
                    className="mt-0.5"
                    style={{
                      color: '#FFFFFF',
                      fontSize: 18,
                      fontWeight: 700,
                      fontFamily: 'Manrope, sans-serif',
                    }}
                  >
                    0
                  </p>
                </div>
              </div>
            </div>
          </header>

          {/* Empty body — OnboardingBlock is the full content for new users */}
          <section
            className="flex-1 flex flex-col"
            style={{ padding: '16px 20px 120px 20px' }}
          >
            <div className="w-full text-left">
              <OnboardingBlock {...onboardingProps} />
            </div>

          </section>

          {/* Bottom Navigation */}
          <nav
            className="fixed bottom-0 left-0 right-0 flex justify-around items-center"
            style={{
              backgroundColor: '#FFFFFF',
              borderTop: '1px solid #F3F4F6',
              paddingTop: 10,
              paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))',
              zIndex: 50,
            }}
            data-testid="bottom-nav"
          >
            {NAV_ITEMS.map((item) => {
              const active = activeNav === item.key;
              const isDisabled = navDisabled && item.key !== 'home' && item.key !== 'cards';
              return (
                <button
                  key={item.key}
                  onClick={() => onNavClick(item.key)}
                  disabled={isDisabled}
                  className="flex flex-col items-center gap-1"
                  style={{
                    opacity: isDisabled ? 0.4 : 1,
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                  }}
                  data-testid={`bottom-nav-${item.key}`}
                >
                  <item.icon
                    className="w-5 h-5"
                    style={{ color: active ? PRIMARY : '#9CA3AF' }}
                    fill={active ? PRIMARY : 'none'}
                  />
                  <span
                    style={{
                      color: active ? PRIMARY : '#9CA3AF',
                      fontSize: 11,
                      fontFamily: 'DM Sans, sans-serif',
                      fontWeight: active ? 600 : 500,
                    }}
                  >
                    {item.label}
                  </span>
                </button>
              );
            })}
          </nav>
          <ScanReceiptModal isOpen={showScanModal} onClose={() => setShowScanModal(false)} isMobile={true} onSaved={fetchDashboard} />

          <GroupSwitcherSheet
            isOpen={showGroupSwitcher}
            onClose={() => setShowGroupSwitcher(false)}
            groups={groups}
            selectedGroupId={selectedGroup?.id}
            onSelect={handleGroupSelect}
            onCreateGroup={() => { setShowGroupSwitcher(false); navigate('/groups/create'); }}
            onManageGroups={() => { setShowGroupSwitcher(false); navigate('/profile'); }}
            userName={userName}
            avatarUrl={avatarUrl}
          />

          <CardDetailSheet
            card={selectedCard}
            isOpen={!!selectedCard}
            onClose={() => setSelectedCard(null)}
            onDeleted={(id) => setLoyaltyCards((prev) => prev.filter((c) => c.id !== id))}
            onUpdated={(updated) => setLoyaltyCards((prev) => prev.map((c) => (c.id === updated.id ? updated : c)))}
          />

          {/* WhatsNew bottom sheet */}
          {showWhatsNewSheet && (
            <>
              <div onClick={() => setShowWhatsNewSheet(false)} className="fixed inset-0 z-50" style={{ backgroundColor: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)' }} />
              <div className="fixed left-0 right-0 bottom-0 z-50" style={{ backgroundColor: '#FFFFFF', borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: '12px 20px', paddingBottom: 'calc(32px + env(safe-area-inset-bottom, 0px))', boxShadow: '0 -8px 32px rgba(0,0,0,0.15)' }}>
                <div className="flex justify-center mb-4"><div style={{ width: 36, height: 4, borderRadius: 999, backgroundColor: '#D1D5DB' }} /></div>
                <div style={{ background: GRADIENT_HEADER, borderRadius: 18, padding: 16, boxShadow: '0 8px 22px rgba(45,91,255,0.28)' }}>
                  <div className="flex items-center gap-2" style={{ marginBottom: 6 }}>
                    <Sparkles className="w-4 h-4" style={{ color: '#FFFFFF' }} />
                    <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: 12, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', fontFamily: 'DM Sans, sans-serif' }}>New</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center flex-shrink-0" style={{ width: 44, height: 44, borderRadius: 14, background: 'rgba(255,255,255,0.18)' }}>
                      <Users className="w-6 h-6" style={{ color: '#FFFFFF' }} />
                    </div>
                    <div className="flex-1">
                      <h3 style={{ color: '#FFFFFF', fontSize: 16, fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>Buddy Circles are here</h3>
                      <p className="mt-0.5" style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, lineHeight: 1.4, fontFamily: 'DM Sans, sans-serif' }}>Share receipts with family or flatmates. Invite someone by email.</p>
                    </div>
                  </div>
                  <button onClick={() => { handleWhatsNewDismiss(); navigate('/groups/create'); }} className="mt-3 w-full flex items-center justify-center gap-1 rounded-full" style={{ backgroundColor: '#FFFFFF', padding: '10px 0' }}>
                    <span style={{ color: '#2D5BFF', fontSize: 14, fontWeight: 700, fontFamily: 'DM Sans, sans-serif' }}>Create a Circle</span>
                    <ChevronRight className="w-4 h-4" style={{ color: '#2D5BFF' }} />
                  </button>
                </div>
                <button onClick={handleWhatsNewDismiss} className="w-full mt-3" style={{ color: '#9CA3AF', fontSize: 14, fontWeight: 600, fontFamily: 'DM Sans, sans-serif', padding: '10px 0' }}>Dismiss</button>
              </div>
            </>
          )}
        </div>
      );
    }

    return (
      <div
        className="min-h-screen flex flex-col"
        style={{ backgroundColor: '#F9FAFB' }}
        data-testid="dashboard-page"
      >
        {/* Gradient Header with translucent stat card inside */}
        <header
          style={{
            background: GRADIENT_HEADER,
            padding: '32px 20px 24px 20px',
            borderBottomLeftRadius: 28,
            borderBottomRightRadius: 28,
          }}
        >
          <div className="flex items-start justify-between mb-5">
            <div>
              <p
                style={{
                  color: 'rgba(255,255,255,0.8)',
                  fontSize: 14,
                  fontFamily: 'DM Sans, sans-serif',
                }}
              >
                {isGroupView ? 'Shared space' : getGreeting()}
              </p>
              <h1
                className="mt-1"
                style={{
                  color: '#FFFFFF',
                  fontSize: 26,
                  fontWeight: 600,
                  fontFamily: 'Manrope, sans-serif',
                  lineHeight: 1.2,
                }}
                data-testid="dashboard-greeting"
              >
                {isGroupView ? selectedGroup.name : `Hello, ${userName}!`}
              </h1>
              {isGroupView && groupData && (
                <MemberAvatarsRow
                  members={groupData.members}
                  count={groupData.members?.length || 0}
                />
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {headerIcon && !isGroupView && (
                <button
                  onClick={() => headerIcon === 'whatsnew' ? setShowWhatsNewSheet(true) : handleA2HSTap()}
                  className="relative flex items-center justify-center"
                  style={{ width: 34, height: 34, borderRadius: 999, backgroundColor: 'rgba(255,255,255,0.18)' }}
                  aria-label={headerIcon === 'whatsnew' ? "What's new" : "Add to Home Screen"}
                >
                  {headerIcon === 'whatsnew' ? (
                    <>
                      <Sparkles className="w-4 h-4" style={{ color: '#FFFFFF' }} />
                      <span className="absolute" style={{ top: 5, right: 5, width: 7, height: 7, borderRadius: 999, backgroundColor: '#F59E0B', border: '1.5px solid rgba(45,91,255,0.7)' }} />
                    </>
                  ) : (
                    <Smartphone className="w-4 h-4" style={{ color: '#FFFFFF' }} />
                  )}
                </button>
              )}
              <GroupSwitcherPill
                selectedGroup={selectedGroup}
                avatarUrl={avatarUrl}
                userName={userName}
                onClick={() => setShowGroupSwitcher(true)}
              />
            </div>
          </div>

          {/* Translucent stat card inside hero */}
          <div
            className="flex items-stretch"
            style={{
              backgroundColor: 'rgba(255,255,255,0.15)',
              borderRadius: 18,
              padding: '16px 18px',
              gap: 16,
            }}
            data-testid="summary-card"
          >
            <div className="flex-1">
              <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, fontFamily: 'DM Sans, sans-serif' }}>
                {isGroupView ? 'Circle spend' : 'Spent this month'}
              </p>
              <p
                className="mt-1"
                style={{ color: '#FFFFFF', fontSize: 30, fontWeight: 700, fontFamily: 'Manrope, sans-serif', lineHeight: 1.1 }}
                data-testid="summary-amount"
              >
                {isGroupView
                  ? formatCurrency(groupData?.spending?.this_month_total || 0, groupData?.spending?.currency || 'GBP')
                  : formatCurrency(currentSummary.total, activeCurrency)
                }
              </p>
              <p className="mt-2" style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, fontFamily: 'DM Sans, sans-serif' }}>
                {monthLabel}
              </p>
            </div>
            <div style={{ width: 1, backgroundColor: 'rgba(255,255,255,0.2)' }} />
            <div className="flex-1">
              <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, fontFamily: 'DM Sans, sans-serif' }}>
                Receipts
              </p>
              <p
                className="mt-1"
                style={{ color: '#FFFFFF', fontSize: 22, fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}
                data-testid="summary-count"
              >
                {isGroupView ? (groupData?.spending?.this_month_count || 0) : currentSummary.count}
              </p>
              <p className="mt-2" style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, fontFamily: 'DM Sans, sans-serif' }}>
                {isGroupView ? 'shared' : 'this month'}
              </p>
            </div>
          </div>
        </header>

        {/* Currency filter chips */}
        {!isGroupView && allCurrencies.length > 1 && (
          <div className="px-5 mt-5">
            <CurrencyChips
              currencies={allCurrencies}
              selected={activeCurrency}
              onSelect={setSelectedCurrency}
            />
          </div>
        )}

        {/* ── GROUP VIEW CONTENT ── */}
        {isGroupView ? (
          <div style={{ paddingBottom: 180 }}>
            {groupLoading ? (
              <div className="flex items-center justify-center py-16">
                <ScanLine className="w-8 h-8 animate-spin" style={{ color: PRIMARY }} />
              </div>
            ) : (
              <>
                {/* Shared vouchers */}
                {(groupData?.vouchers?.length > 0) && (
                  <div className="px-5 mt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h2 style={{ color: '#111827', fontSize: 16, fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>Shared vouchers</h2>
                      <button onClick={handleVouchersClick} style={{ color: PRIMARY, fontSize: 14, fontWeight: 600, fontFamily: 'DM Sans, sans-serif' }}>See all</button>
                    </div>
                    <div className="flex items-center gap-3 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
                      {groupData.vouchers.slice(0, 5).map((v) => (
                        <div key={v.share_id} className="flex-shrink-0" style={{
                          width: 160, borderRadius: 14, backgroundColor: '#EEF2FF',
                          padding: 12, border: '1px solid #C7D2FE',
                        }}>
                          <p style={{ color: '#1E3A8A', fontSize: 13, fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>
                            {v.item?.retailer || 'Voucher'}
                          </p>
                          <p style={{ color: '#3B82F6', fontSize: 12, fontFamily: 'DM Sans, sans-serif', marginTop: 4 }}>
                            {v.item?.value_label || formatCurrency(v.item?.value, v.item?.currency)}
                          </p>
                          <p style={{ color: '#9CA3AF', fontSize: 11, fontFamily: 'DM Sans, sans-serif', marginTop: 4 }}>
                            by {v.shared_by_name}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Shared cards */}
                {(groupData?.cards?.length > 0) && (
                  <div className="px-5 mt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h2 style={{ color: '#111827', fontSize: 16, fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>Shared cards</h2>
                      <button onClick={handleCardsClick} style={{ color: PRIMARY, fontSize: 14, fontWeight: 600, fontFamily: 'DM Sans, sans-serif' }}>See all</button>
                    </div>
                    <div className="flex items-center gap-3 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
                      {groupData.cards.map((c) => {
                        const card = c.item || {};
                        const name = card.card_name || card.name || '?';
                        const initials = name.slice(0, 2).toUpperCase();
                        const _brand = getBrandGradient(name);
                        const _tileGradient = _brand
                          ? `linear-gradient(135deg, ${_brand.from} 0%, ${_brand.to} 100%)`
                          : getCardGradient(card);
                        return (
                          <div key={c.share_id} className="relative flex-shrink-0 overflow-hidden" style={{
                            width: 160, height: 90, borderRadius: 14,
                            background: _tileGradient, boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
                          }}>
                            <div className="absolute inset-0" style={{ padding: 12 }}>
                              <div className="flex items-start justify-between">
                                <div className="flex items-center justify-center" style={{
                                  width: 26, height: 26, borderRadius: 999,
                                  backgroundColor: 'rgba(255,255,255,0.2)', color: '#FFFFFF',
                                  fontSize: 10, fontWeight: 700, fontFamily: 'Manrope, sans-serif',
                                }}>{initials}</div>
                                <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: 10, fontWeight: 700, letterSpacing: 1, fontFamily: 'DM Sans, sans-serif' }}>SHARED</span>
                              </div>
                              <div className="absolute" style={{ bottom: 12, left: 12, right: 50 }}>
                                <p style={{ color: '#FFFFFF', fontSize: 15, fontWeight: 700, fontFamily: 'Manrope, sans-serif', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</p>
                              </div>
                              <div className="absolute flex items-center justify-center" style={{ bottom: 10, right: 10, width: 28, height: 28, borderRadius: 999, backgroundColor: 'rgba(255,255,255,0.25)' }}>
                                <Barcode className="w-4 h-4" style={{ color: '#FFFFFF' }} />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Shared receipts */}
                <section className="px-5 mt-6">
                  <div style={{ backgroundColor: '#FFFFFF', borderRadius: 18, padding: 16, boxShadow: '0 4px 16px rgba(15,23,42,0.06)' }}>
                    <div className="flex items-center justify-between mb-2">
                      <h2 style={{ color: '#111827', fontSize: 16, fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>Shared receipts</h2>
                    </div>
                    {groupData?.receipts?.length > 0 ? (
                      groupData.receipts.slice(0, 10).map((r) => {
                        const receipt = r.item || {};
                        return (
                          <button key={r.share_id} onClick={() => handleReceiptClick(receipt)} className="w-full flex items-center justify-between py-3 rounded-lg px-2 -mx-2 hover:bg-gray-50">
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                              <MerchantAvatar name={receipt.merchant} />
                              <div className="text-left min-w-0 flex-1">
                                <p className="font-semibold truncate" style={{ color: '#111827', fontSize: 16, fontFamily: 'Manrope, sans-serif' }}>{receipt.merchant || 'Unknown'}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  {receipt.category && (
                                    <span className="rounded-full" style={{ backgroundColor: getCategoryPill(receipt.category).bg, color: getCategoryPill(receipt.category).color, fontSize: 11, fontWeight: 600, padding: '2px 8px', fontFamily: 'DM Sans, sans-serif' }}>{receipt.category}</span>
                                  )}
                                  {receipt.transaction_date && (
                                    <span style={{ color: '#D1D5DB', fontSize: 12, fontFamily: 'DM Sans, sans-serif' }}>· {formatReceiptDate(receipt.transaction_date)}</span>
                                  )}
                                </div>
                                {r.shared_by_name && (
                                  <div className="mt-0.5">
                                    <span style={{ color: '#9CA3AF', fontSize: 12, fontFamily: 'DM Sans, sans-serif' }}>· by {r.shared_by_name.split(' ')[0]}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span style={{ color: '#111827', fontSize: 16, fontWeight: 600, fontFamily: 'Manrope, sans-serif' }}>
                                {formatCurrency(receipt.amount, receipt.currency)}
                              </span>
                              <ChevronRight className="w-4 h-4" style={{ color: '#D1D5DB' }} />
                            </div>
                          </button>
                        );
                      })
                    ) : (
                      <div className="flex flex-col items-center py-10 text-center">
                        <div className="flex items-center justify-center mb-3" style={{ width: 56, height: 56, borderRadius: 999, backgroundColor: '#EEF2FF' }}>
                          <Receipt className="w-6 h-6" style={{ color: PRIMARY }} />
                        </div>
                        <p style={{ color: '#111827', fontSize: 16, fontWeight: 600, fontFamily: 'Manrope, sans-serif', marginBottom: 4 }}>No shared receipts yet</p>
                        <p style={{ color: '#9CA3AF', fontSize: 13, fontFamily: 'DM Sans, sans-serif', maxWidth: 240, lineHeight: 1.5 }}>
                          Scan a receipt and share it with this group so everyone can see it
                        </p>
                      </div>
                    )}
                  </div>
                </section>

                {/* Empty state for zero shared items overall */}
                {(!groupData?.receipts?.length && !groupData?.cards?.length && !groupData?.vouchers?.length) && (
                  <div className="flex flex-col items-center py-8 px-6 text-center">
                    <div className="flex items-center justify-center mb-3" style={{ width: 64, height: 64, borderRadius: 999, backgroundColor: '#EEF2FF' }}>
                      <Users className="w-7 h-7" style={{ color: PRIMARY }} />
                    </div>
                    <p style={{ color: '#111827', fontSize: 18, fontWeight: 700, fontFamily: 'Manrope, sans-serif', marginBottom: 6 }}>Nothing shared yet</p>
                    <p style={{ color: '#9CA3AF', fontSize: 14, fontFamily: 'DM Sans, sans-serif', maxWidth: 280, lineHeight: 1.6 }}>
                      When you or a group member shares a receipt, card, or voucher it will appear here
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
        /* ── PERSONAL VIEW CONTENT (existing) ── */
        <>

        {/* Onboarding: what's new + get-set-up checklist */}
        <div className="px-5 mt-4">
          <OnboardingBlock {...onboardingProps} />
        </div>

        {/* Quick actions row */}
        <div className="px-5 mt-4">
          <QuickActionsRow
            onScan={handleScanReceipt}
            onCircle={() => navigate('/groups/create')}
            onCard={handleCardsClick}
            onVoucher={handleVouchersClick}
          />
        </div>

        {/* Recent receipts — promoted above feature cards */}
        <section className="px-5 mt-6">
          <div
            style={{
              backgroundColor: '#FFFFFF',
              borderRadius: 18,
              padding: 16,
              boxShadow: '0 4px 16px rgba(15,23,42,0.06)',
            }}
          >
            <div className="flex items-center justify-between mb-2">
              <h2
                style={{
                  color: '#111827',
                  fontSize: 16,
                  fontWeight: 700,
                  fontFamily: 'Manrope, sans-serif',
                }}
              >
                Recent receipts
              </h2>
              <button
                onClick={() => onNavClick('receipts')}
                style={{ color: PRIMARY, fontSize: 14, fontWeight: 600, fontFamily: 'DM Sans, sans-serif' }}
                data-testid="see-all-label"
              >
                See all
              </button>
            </div>

            {loading ? (
              <div>
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
              </div>
            ) : recentReceipts.length === 0 ? (
              <EmptyState onScanClick={handleScanReceipt} />
            ) : (
              <div>
                {recentReceipts.map((receipt) => (
                  <ReceiptRow key={receipt.id} receipt={receipt} onClick={handleReceiptClick} />
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Voucher card — hidden during first-run setup unless there are vouchers */}
        {(!onboardingActive || hasVouchers) && (
          <div className="px-5 mt-4">
            <VoucherSummaryCard
              summary={voucherSummary}
              onPress={handleVouchersClick}
              onAddClick={handleVouchersClick}
              isMobile={true}
            />
          </div>
        )}

        {/* Loyalty: always show real cards; show the promo only when not in setup */}
        {hasCards ? (
          <div className="px-5 mt-4">
            <LoyaltyCardsStrip
              cards={loyaltyCards}
              onSeeAll={handleCardsClick}
              onAdd={handleCardsClick}
              onCardClick={(c) => setSelectedCard(c)}
              cardSize={{ w: 160, h: 90 }}
            />
          </div>
        ) : (!onboardingActive && (
          <div className="px-5 mt-4">
            <LoyaltyPromoBanner onClick={handleCardsClick} fullWidthCta={false} />
          </div>
        ))}

        {/* Bottom spacer so content clears the FAB + nav */}
        <div style={{ height: 180 }} />
        {/* Close personal view conditional */}
        </>
        )}

        {/* FAB above bottom nav */}
        <FAB onClick={handleScanReceipt} bottom={88} />

        {/* Group Switcher Bottom Sheet */}
        <GroupSwitcherSheet
          isOpen={showGroupSwitcher}
          onClose={() => setShowGroupSwitcher(false)}
          groups={groups}
          selectedGroupId={selectedGroup?.id}
          onSelect={handleGroupSelect}
          onCreateGroup={() => { setShowGroupSwitcher(false); navigate('/groups/create'); }}
          onManageGroups={() => { setShowGroupSwitcher(false); navigate('/profile'); }}
          userName={userName}
          avatarUrl={avatarUrl}
        />

        {/* WhatsNew bottom sheet */}
        {showWhatsNewSheet && (
          <>
            <div onClick={() => setShowWhatsNewSheet(false)} className="fixed inset-0 z-50" style={{ backgroundColor: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)' }} />
            <div className="fixed left-0 right-0 bottom-0 z-50" style={{ backgroundColor: '#FFFFFF', borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: '12px 20px', paddingBottom: 'calc(32px + env(safe-area-inset-bottom, 0px))', boxShadow: '0 -8px 32px rgba(0,0,0,0.15)' }}>
              <div className="flex justify-center mb-4"><div style={{ width: 36, height: 4, borderRadius: 999, backgroundColor: '#D1D5DB' }} /></div>
              <div style={{ background: GRADIENT_HEADER, borderRadius: 18, padding: 16, boxShadow: '0 8px 22px rgba(45,91,255,0.28)' }}>
                <div className="flex items-center gap-2" style={{ marginBottom: 6 }}>
                  <Sparkles className="w-4 h-4" style={{ color: '#FFFFFF' }} />
                  <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: 12, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', fontFamily: 'DM Sans, sans-serif' }}>New</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center flex-shrink-0" style={{ width: 44, height: 44, borderRadius: 14, background: 'rgba(255,255,255,0.18)' }}>
                    <Users className="w-6 h-6" style={{ color: '#FFFFFF' }} />
                  </div>
                  <div className="flex-1">
                    <h3 style={{ color: '#FFFFFF', fontSize: 16, fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>Buddy Circles are here</h3>
                    <p className="mt-0.5" style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, lineHeight: 1.4, fontFamily: 'DM Sans, sans-serif' }}>Share receipts with family or flatmates. Invite someone by email.</p>
                  </div>
                </div>
                <button onClick={() => { handleWhatsNewDismiss(); navigate('/groups/create'); }} className="mt-3 w-full flex items-center justify-center gap-1 rounded-full" style={{ backgroundColor: '#FFFFFF', padding: '10px 0' }}>
                  <span style={{ color: '#2D5BFF', fontSize: 14, fontWeight: 700, fontFamily: 'DM Sans, sans-serif' }}>Create a Circle</span>
                  <ChevronRight className="w-4 h-4" style={{ color: '#2D5BFF' }} />
                </button>
              </div>
              <button onClick={handleWhatsNewDismiss} className="w-full mt-3" style={{ color: '#9CA3AF', fontSize: 14, fontWeight: 600, fontFamily: 'DM Sans, sans-serif', padding: '10px 0' }}>Dismiss</button>
            </div>
          </>
        )}

        {/* Receipt Detail */}
        <ReceiptDetailModal
          isOpen={showReceiptDetail}
          onClose={() => setShowReceiptDetail(false)}
          receiptId={selectedReceiptId}
          onDelete={handleReceiptDelete}
          isMobile={true}
        />

        {/* Bottom Navigation */}
        <nav
          className="fixed bottom-0 left-0 right-0 flex justify-around items-center"
          style={{
            backgroundColor: '#FFFFFF',
            borderTop: '1px solid #F3F4F6',
            paddingTop: 10,
            paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))',
            zIndex: 50,
          }}
          data-testid="bottom-nav"
        >
          {NAV_ITEMS.map((item) => {
            const active = activeNav === item.key;
            return (
              <button
                key={item.key}
                onClick={() => onNavClick(item.key)}
                className="flex flex-col items-center gap-1"
                data-testid={`bottom-nav-${item.key}`}
              >
                <item.icon
                  className="w-5 h-5"
                  style={{ color: active ? PRIMARY : '#9CA3AF' }}
                  fill={active ? PRIMARY : 'none'}
                />
                <span
                  style={{
                    color: active ? PRIMARY : '#9CA3AF',
                    fontSize: 11,
                    fontFamily: 'DM Sans, sans-serif',
                    fontWeight: active ? 600 : 500,
                  }}
                >
                  {item.label}
                </span>
              </button>
            );
          })}
        </nav>
        <ScanReceiptModal isOpen={showScanModal} onClose={() => setShowScanModal(false)} isMobile={true} onSaved={fetchDashboard} />

        <CardDetailSheet
          card={selectedCard}
          isOpen={!!selectedCard}
          onClose={() => setSelectedCard(null)}
          onDeleted={(id) => setLoyaltyCards((prev) => prev.filter((c) => c.id !== id))}
          onUpdated={(updated) => setLoyaltyCards((prev) => prev.map((c) => (c.id === updated.id ? updated : c)))}
        />

        <CouldntReadModal
          receipt={couldntReadReceipt}
          isOpen={!!couldntReadReceipt}
          onClose={() => setCouldntReadReceipt(null)}
          onChanged={() => { setCouldntReadReceipt(null); fetchDashboard(); }}
        />
      </div>
    );
  }

  // ------------ DESKTOP ------------
  return (
    <div
      className="min-h-screen flex"
      style={{ backgroundColor: '#F3F4F6' }}
      data-testid="dashboard-page"
    >
      {/* Sidebar */}
      <aside
        className="flex flex-col"
        style={{ width: 240, backgroundColor: '#F9FAFB', borderRight: '1px solid #F3F4F6' }}
      >
        <div className="flex items-center gap-2 px-5 pt-6 pb-8">
          <BuddyLogo size={28} />
          <span
            style={{
              color: '#111827',
              fontSize: 18,
              fontWeight: 600,
              fontFamily: 'Manrope, sans-serif',
            }}
          >
            My Buddy
          </span>
        </div>

        <nav className="flex-1 px-3">
          {NAV_ITEMS.map((item) => {
            const active = activeNav === item.key;
            const isDisabled = navDisabled && item.key !== 'home';
            return (
              <button
                key={item.key}
                onClick={() => onNavClick(item.key)}
                disabled={isDisabled}
                className="w-full flex items-center gap-3 px-4 py-3 mb-1 transition-all"
                style={{
                  backgroundColor: active ? '#E0E7FF' : 'transparent',
                  color: active ? PRIMARY : '#6B7280',
                  borderRadius: 10,
                  fontSize: 14,
                  fontWeight: active ? 600 : 500,
                  fontFamily: 'DM Sans, sans-serif',
                  opacity: isDisabled ? 0.4 : 1,
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                }}
                data-testid={`sidebar-nav-${item.key}`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </button>
            );
          })}
        </nav>

        <div className="p-4 border-t" style={{ borderColor: '#F3F4F6' }}>
          <div className="flex items-center gap-3">
            <UserAvatar name={userName} avatarUrl={avatarUrl} size={40} fontSize={14} />
            <div className="min-w-0">
              <p
                className="truncate"
                style={{
                  color: '#111827',
                  fontSize: 14,
                  fontWeight: 600,
                  fontFamily: 'Manrope, sans-serif',
                }}
                data-testid="sidebar-user-name"
              >
                {userName} {lastName}
              </p>
              {userEmail ? (
                <p
                  className="truncate"
                  style={{
                    color: '#6B7280',
                    fontSize: 12,
                    fontFamily: 'DM Sans, sans-serif',
                  }}
                  data-testid="sidebar-user-email"
                >
                  {userEmail}
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-y-auto">
        {!loading && recentReceipts.length === 0 ? (
          <>
            {/* Gradient header with embedded stat cards */}
            <div style={{ background: GRADIENT_HEADER, padding: '36px 40px 28px' }}>
              <div className="flex items-start justify-between">
                <div>
                  <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14, fontFamily: 'DM Sans, sans-serif' }}>
                    {isGroupView ? 'Shared space' : getGreeting()}
                  </p>
                  <h1
                    className="mt-1"
                    style={{ color: '#FFFFFF', fontSize: 32, fontWeight: 600, fontFamily: 'Manrope, sans-serif' }}
                    data-testid="dashboard-greeting"
                  >
                    {isGroupView ? selectedGroup.name : `Hello, ${userName}!`}
                  </h1>
                </div>
                <GroupSwitcherPill
                  selectedGroup={selectedGroup}
                  avatarUrl={avatarUrl}
                  userName={userName}
                  onClick={() => setShowGroupSwitcher(true)}
                />
              </div>
            </div>

            {/* Stat strip */}
            <div
              className="flex items-stretch"
              style={{
                backgroundColor: '#FFFFFF',
                padding: '20px 40px',
                borderBottom: '1px solid #F3F4F6',
              }}
              data-testid="empty-stat-strip"
            >
              <div className="flex-1">
                <p style={{ color: '#6B7280', fontSize: 13, fontFamily: 'DM Sans, sans-serif' }}>Spent this month</p>
                <p
                  className="mt-1"
                  style={{
                    color: '#111827',
                    fontSize: 28,
                    fontWeight: 700,
                    fontFamily: 'Manrope, sans-serif',
                    lineHeight: 1.1,
                  }}
                  data-testid="empty-spent-amount"
                >
                  {formatCurrency(0, activeCurrency)}
                </p>
                <p className="mt-2" style={{ color: '#9CA3AF', fontSize: 12, fontFamily: 'DM Sans, sans-serif' }} data-testid="empty-month-label">
                  {monthLabel}
                </p>
              </div>
              <div style={{ display: 'flex', gap: 16, marginTop: 20 }}>
                <div style={{ flex: 1, backgroundColor: '#FFFFFF', borderRadius: 14, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, backgroundColor: '#EEF2FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <CreditCard className="w-5 h-5" style={{ color: PRIMARY }} />
                  </div>
                  <div>
                    <p style={{ color: '#6B7280', fontSize: 12, fontFamily: 'DM Sans, sans-serif' }}>Spent this month</p>
                    <p style={{ color: '#111827', fontSize: 22, fontWeight: 700, fontFamily: 'Manrope, sans-serif', lineHeight: 1.1 }} data-testid="empty-spent-amount">
                      {formatCurrency(0, activeCurrency)}
                    </p>
                    <p style={{ color: '#9CA3AF', fontSize: 11, fontFamily: 'DM Sans, sans-serif', marginTop: 2 }} data-testid="empty-month-label">
                      {monthLabel}
                    </p>
                  </div>
                </div>
                <div style={{ flex: 1, backgroundColor: '#FFFFFF', borderRadius: 14, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, backgroundColor: '#ECFDF5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <FileText className="w-5 h-5" style={{ color: '#059669' }} />
                  </div>
                  <div>
                    <p style={{ color: '#6B7280', fontSize: 12, fontFamily: 'DM Sans, sans-serif' }}>Receipts</p>
                    <p style={{ color: '#111827', fontSize: 22, fontWeight: 700, fontFamily: 'Manrope, sans-serif', lineHeight: 1.1 }}>
                      0
                    </p>
                    <p style={{ color: '#9CA3AF', fontSize: 11, fontFamily: 'DM Sans, sans-serif', marginTop: 2 }}>
                      This month
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Voucher card */}
            <div style={{ padding: '24px 40px 0' }}>
              <VoucherSummaryCard
                summary={voucherSummary}
                onPress={handleVouchersClick}
                onAddClick={handleVouchersClick}
                isMobile={false}
              />
            </div>

            {/* Centered empty card */}
            <div className="flex justify-center" style={{ padding: '40px 40px 80px 40px' }}>
              <div
                className="w-full text-center"
                style={{
                  maxWidth: 560,
                  backgroundColor: '#FFFFFF',
                  borderRadius: 18,
                  padding: '48px 40px',
                  boxShadow: '0px 4px 16px rgba(0,0,0,0.05)',
                }}
                data-testid="empty-card-desktop"
              >
                <div className="flex justify-center">
                  <BuddyLogo size={120} />
                </div>
                <h2
                  className="mt-6"
                  style={{
                    color: '#111827',
                    fontSize: 24,
                    fontWeight: 700,
                    fontFamily: 'Manrope, sans-serif',
                  }}
                  data-testid="empty-title"
                >
                  No receipts yet
                </h2>
                <p
                  className="mt-3 mx-auto"
                  style={{
                    color: '#6B7280',
                    fontSize: 15,
                    fontFamily: 'DM Sans, sans-serif',
                    maxWidth: 420,
                    lineHeight: 1.5,
                  }}
                  data-testid="empty-message"
                >
                  Scan your first receipt and I'll capture all the details for you — merchant, items, totals, everything
                </p>
                <button
                  onClick={handleScanReceipt}
                  className="mt-7 inline-flex items-center justify-center gap-2 rounded-full"
                  style={{
                    background: GRADIENT_HEADER,
                    color: '#FFFFFF',
                    padding: '14px 36px',
                    fontSize: 16,
                    fontWeight: 700,
                    fontFamily: 'DM Sans, sans-serif',
                    boxShadow: '0px 8px 20px rgba(45,91,255,0.35)',
                  }}
                  data-testid="scan-first-receipt-btn"
                >
                  <ScanLine className="w-5 h-5" />
                  Scan first receipt
                </button>
                <div
                  className="mt-6 inline-flex items-center gap-2 rounded-full"
                  style={{ backgroundColor: '#F3F4F6', padding: '8px 16px' }}
                  data-testid="empty-merchants-pill"
                >
                  <CheckCircle2 className="w-4 h-4" style={{ color: '#9CA3AF' }} />
                  <span style={{ color: '#6B7280', fontSize: 13, fontFamily: 'DM Sans, sans-serif' }}>
                    Works with any UK receipt — Tesco, ASDA, Waitrose, restaurants and more
                  </span>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Gradient header with embedded stat cards */}
            <div style={{ background: GRADIENT_HEADER, padding: '36px 40px 28px' }}>
              <div className="flex items-start justify-between">
                <div>
                  <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14, fontFamily: 'DM Sans, sans-serif' }}>
                    {getGreeting()}
                  </p>
                  <h1
                    className="mt-1"
                    style={{ color: '#FFFFFF', fontSize: 32, fontWeight: 600, fontFamily: 'Manrope, sans-serif' }}
                    data-testid="dashboard-greeting"
                  >
                    Hello, {userName}!
                  </h1>
                </div>
                <GroupSwitcherPill
                  selectedGroup={selectedGroup}
                  avatarUrl={avatarUrl}
                  userName={userName}
                  onClick={() => setShowGroupSwitcher(true)}
                />
              </div>
            </div>

            {/* Stat strip */}
            <div
              className="flex items-stretch"
              style={{
                backgroundColor: '#FFFFFF',
                padding: '20px 40px',
                borderBottom: '1px solid #F3F4F6',
              }}
              data-testid="summary-card"
            >
              <div className="flex-1">
                <p style={{ color: '#6B7280', fontSize: 13, fontFamily: 'DM Sans, sans-serif' }}>Spent this month</p>
                <p
                  className="mt-1"
                  style={{ color: '#111827', fontSize: 28, fontWeight: 700, fontFamily: 'Manrope, sans-serif', lineHeight: 1.1 }}
                  data-testid="summary-amount"
                >
                  {formatCurrency(currentSummary.total, activeCurrency)}
                </p>
                <p className="mt-2" style={{ color: '#9CA3AF', fontSize: 12, fontFamily: 'DM Sans, sans-serif' }}>
                  {monthLabel}
                </p>
              </div>
              <div style={{ display: 'flex', gap: 16, marginTop: 20 }}>
                <div style={{ flex: 1, backgroundColor: '#FFFFFF', borderRadius: 14, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, backgroundColor: '#EEF2FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <CreditCard className="w-5 h-5" style={{ color: PRIMARY }} />
                  </div>
                  <div>
                    <p style={{ color: '#6B7280', fontSize: 12, fontFamily: 'DM Sans, sans-serif' }}>Spent this month</p>
                    <p style={{ color: '#111827', fontSize: 22, fontWeight: 700, fontFamily: 'Manrope, sans-serif', lineHeight: 1.1 }} data-testid="summary-amount">
                      {formatCurrency(currentSummary.total, activeCurrency)}
                    </p>
                    <p style={{ color: '#9CA3AF', fontSize: 11, fontFamily: 'DM Sans, sans-serif', marginTop: 2 }}>
                      {monthLabel}
                    </p>
                  </div>
                </div>
                <div style={{ flex: 1, backgroundColor: '#FFFFFF', borderRadius: 14, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, backgroundColor: '#ECFDF5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <FileText className="w-5 h-5" style={{ color: '#059669' }} />
                  </div>
                  <div>
                    <p style={{ color: '#6B7280', fontSize: 12, fontFamily: 'DM Sans, sans-serif' }}>Receipts</p>
                    <p style={{ color: '#111827', fontSize: 22, fontWeight: 700, fontFamily: 'Manrope, sans-serif', lineHeight: 1.1 }} data-testid="summary-count">
                      {currentSummary.count}
                    </p>
                    <p style={{ color: '#9CA3AF', fontSize: 11, fontFamily: 'DM Sans, sans-serif', marginTop: 2 }}>
                      This month
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Onboarding: what's new + get-set-up checklist */}
            <div style={{ padding: '24px 40px 0' }}>
              <OnboardingBlock {...onboardingProps} />
            </div>

            {/* Quick actions row */}
            <div style={{ padding: '20px 40px 0' }}>
              <QuickActionsRow
                onScan={handleScanReceipt}
                onCircle={() => navigate('/groups/create')}
                onCard={handleCardsClick}
                onVoucher={handleVouchersClick}
              />
            </div>

            {/* Currency chips — only when more than one currency */}
            {allCurrencies.length > 1 && (
              <div style={{ padding: '20px 40px 0' }}>
                <CurrencyChips
                  currencies={allCurrencies}
                  selected={activeCurrency}
                  onSelect={setSelectedCurrency}
                />
              </div>
            )}

            {/* Feature cards — hidden during first-run setup to avoid duplicate CTAs */}
            {(!onboardingActive || hasVouchers || hasCards) && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, padding: '24px 40px 0' }}>
              {/* Left: vouchers */}
              <div>
                {(!onboardingActive || hasVouchers) && (
                  <VoucherSummaryCard
                    summary={voucherSummary}
                    onPress={handleVouchersClick}
                    onAddClick={handleVouchersClick}
                    isMobile={false}
                  />
                )}
              </div>

              {/* Right: loyalty cards 2x2 grid or promo */}
              <div>
                {hasCards ? (
                  <LoyaltyCardsGrid
                    cards={loyaltyCards}
                    onSeeAll={handleCardsClick}
                    onAdd={handleCardsClick}
                    onCardClick={(c) => setSelectedCard(c)}
                  />
                ) : (!onboardingActive && (
                  <LoyaltyPromoBanner onClick={handleCardsClick} fullWidthCta={true} />
                ))}
              </div>
            </div>
            )}

            {/* Recent receipts — full width */}
            <section style={{ padding: '24px 40px', paddingBottom: 120 }}>
              <div
                style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: 18,
                  padding: 24,
                  boxShadow: '0 8px 24px rgba(15,23,42,0.08)',
                }}
              >
                <div className="flex items-center justify-between mb-3">
                  <h2
                    style={{
                      color: '#111827',
                      fontSize: 16,
                      fontWeight: 700,
                      fontFamily: 'Manrope, sans-serif',
                    }}
                  >
                    Recent receipts
                  </h2>
                  <button
                    onClick={() => onNavClick('receipts')}
                    style={{ color: PRIMARY, fontSize: 14, fontWeight: 600, fontFamily: 'DM Sans, sans-serif' }}
                    data-testid="see-all-label"
                  >
                    See all
                  </button>
                </div>

                {loading ? (
                  <div>
                    <SkeletonRow />
                    <SkeletonRow />
                    <SkeletonRow />
                  </div>
                ) : recentReceipts.length === 0 ? (
                  <EmptyState onScanClick={handleScanReceipt} />
                ) : (
                  <div>
                    <div
                      className="grid items-center text-left mb-1"
                      style={{
                        gridTemplateColumns: '2fr 1fr 1fr 1fr auto',
                        padding: '8px 4px',
                        color: '#9CA3AF',
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: 1,
                        fontFamily: 'DM Sans, sans-serif',
                        borderBottom: '1px solid #F1F5F9',
                      }}
                    >
                      <span>MERCHANT</span>
                      <span>DATE</span>
                      <span>CATEGORY</span>
                      <span className="text-right">AMOUNT</span>
                      <span style={{ width: 16 }} />
                    </div>
                    {recentReceipts.map((receipt) => {
                      const pill = getCategoryPill(receipt.category);
                      const isPending = receipt.scan_status === 'pending_retry' || receipt.scan_status === 'needs_review';
                      const isFailed = receipt.scan_status === 'failed';
                      return (
                        <button
                          key={receipt.id}
                          onClick={() => handleReceiptClick(receipt)}
                          className="w-full grid items-center text-left transition-colors hover:bg-gray-50 rounded-lg"
                          style={{
                            gridTemplateColumns: '2fr 1fr 1fr 1fr auto',
                            padding: '12px 4px',
                            borderBottom: '1px solid #F1F5F9',
                          }}
                          data-testid={`receipt-item-${receipt.id}`}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <MerchantAvatar name={receipt.merchant} />
                            <span
                              className="font-semibold truncate"
                              style={{ color: '#111827', fontSize: 15, fontFamily: 'Manrope, sans-serif' }}
                            >
                              {isPending ? 'Processing…' : isFailed ? (receipt.merchant && receipt.merchant !== 'Processing…' ? receipt.merchant : 'Unknown') : receipt.merchant}
                            </span>
                          </div>
                          <span style={{ color: '#6B7280', fontSize: 14, fontFamily: 'DM Sans, sans-serif' }}>
                            {!isPending ? formatReceiptDate(receipt.transaction_date || receipt.created_at) : '—'}
                          </span>
                          <span>
                            {isPending || isFailed ? (
                              <ReceiptStatusBadge status={receipt.scan_status} size="xs" />
                            ) : receipt.category ? (
                              <span
                                className="rounded-full inline-block"
                                style={{
                                  backgroundColor: pill.bg,
                                  color: pill.color,
                                  fontSize: 12,
                                  fontWeight: 600,
                                  padding: '3px 10px',
                                  fontFamily: 'DM Sans, sans-serif',
                                }}
                              >
                                {receipt.category}
                              </span>
                            ) : null}
                          </span>
                          <span
                            className="text-right font-semibold"
                            style={{ color: '#111827', fontSize: 15, fontFamily: 'Manrope, sans-serif' }}
                          >
                            {!isPending && !isFailed ? formatCurrency(receipt.amount, receipt.currency || activeCurrency) : '—'}
                          </span>
                          <ChevronRight className="w-5 h-5 ml-2" style={{ color: '#D1D5DB' }} />
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </section>
          </>
        )}
      </main>

      {/* FAB */}
      <FAB onClick={handleScanReceipt} bottom={32} />

      <ScanReceiptModal isOpen={showScanModal} onClose={() => setShowScanModal(false)} isMobile={isMobile} onSaved={fetchDashboard} />

      <CardDetailSheet
        card={selectedCard}
        isOpen={!!selectedCard}
        onClose={() => setSelectedCard(null)}
        onDeleted={(id) => setLoyaltyCards((prev) => prev.filter((c) => c.id !== id))}
        onUpdated={(updated) => setLoyaltyCards((prev) => prev.map((c) => (c.id === updated.id ? updated : c)))}
      />

      <CouldntReadModal
        receipt={couldntReadReceipt}
        isOpen={!!couldntReadReceipt}
        onClose={() => setCouldntReadReceipt(null)}
        onChanged={() => { setCouldntReadReceipt(null); fetchDashboard(); }}
      />

      <GroupSwitcherSheet
        isOpen={showGroupSwitcher}
        onClose={() => setShowGroupSwitcher(false)}
        groups={groups}
        selectedGroupId={selectedGroup?.id}
        onSelect={handleGroupSelect}
        onCreateGroup={() => { setShowGroupSwitcher(false); navigate('/groups/create'); }}
        onManageGroups={() => { setShowGroupSwitcher(false); navigate('/profile'); }}
        userName={userName}
        avatarUrl={avatarUrl}
      />
    </div>
  );
}